import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  BoTokenAtom,
  BoUserIdAtom,
  BoUserTypeAtom,
  CounselEnrollAtom,
  CounselProgressAtom,
  UserObjectIdAtom,
} from "src/Atoms";
import useAxios from "src/hooks/useAxios";
import { clearLocalStorage } from "src/common/Util";
import { cn } from "src/lib/utils";
import { useAfterHydration } from "src/common/prerender";
import { IcArrowDiagonal } from "src/v1/features/Landing/icons/IcArrowDiagonal";
import { Button } from "../shadcn-button";
import { DropdownMenuAvatar } from "./DropdownMenuAvatar";

const Navbar = ({ sticky = true }: { sticky?: boolean }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const boUserId = useRecoilValue(BoUserIdAtom);
  const { axiosGet } = useAxios();
  const [userInfo, setUserInfo] = useState({});
  const [boUserType, setBoUserType] = useRecoilState(BoUserTypeAtom);
  const setBoToken = useSetRecoilState(BoTokenAtom);
  const setUserObjectId = useSetRecoilState(UserObjectIdAtom);
  const setEnrolledCount = useSetRecoilState(CounselEnrollAtom);
  const setProgressCount = useSetRecoilState(CounselProgressAtom);
  // The prerendered header is always the logged-out one; switch to the real login state after hydration
  const displayUserType = useAfterHydration(boUserType, "anonymous");

  // Handle scroll detection with throttling for smoother performance
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          setIsScrolled(scrollTop > 10); // Small threshold for better UX
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    getUserInfo();
  }, [boUserType]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside or on a link
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const getUserInfo = async () => {
    //새 사용자이면, user가 존재하는지 체크.
    console.log("###info ERROR1");

    //ISMS.1 이전코드
    let accountTableData = await axiosGet("/vws/bo/user/info", {
      // username: boUserId,
      anonymous: boUserId,
    });

    //ISMS. 적용코드
    // let accountTableData = await axiosGet('/vws/bo/user/infoAuto');
    // console.log('###infoAuto', accountTableData?.idx);

    setUserInfo(accountTableData);

    //enrollCount, progressCount 도 여기서 가져와도 되고.
  };

  const onLogout = () => {
    setBoToken("");
    setBoUserType("");
    setUserObjectId("");
    setEnrolledCount(0);
    setProgressCount(0);
    clearLocalStorage();
    //window.location.reload();  // 이건 localStorage["bo-uid"]를 지우기 위해서 불러줌
    window.location.href = "/";
  };

  return (
    <header
      className={cn(
        "w-full left-0 right-0 bg-white mx-auto",
        sticky && "sticky -top-px z-50",
      )}
    >
      {/* Animated border */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 h-px bg-gray-200 transition-all duration-500 ease-in-out",
          isScrolled
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform -translate-y-1",
        )}
      />

      <div
        className="flex py-[14px] justify-between mx-auto desktop:max-w-[1170px] gap-2 desktop:gap-8 px-4 tablet:px-8 desktop:px-0"
        style={{ borderColor: "#F5F4F3", color: "#191919" }}
      >
        {/* Logo */}
        <div className="flex items-center tablet:justify-between w-full gap-4">
          <Link
            to="/"
            className="p-0 m-0 flex align-items-center cursor-pointer"
            id="logo_click"
          >
            <img
              src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/logos/header-logo.webp"
              alt="채권도시 법률사무소"
              fetchPriority="high"
              className="h-6 tablet:h-8 ml-0 tablet:ml-2"
            />
          </Link>

          {/* Desktop/Tablet Navigation */}
          <nav className="cursor-pointer gap-4 flex items-center font-bold">
            <Link
              to="/company"
              className="hidden tablet:block relative group transition-all duration-200 hover:text-blue-600"
              id="lawyer_click"
            >
              대표 변호사
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></div>
            </Link>
            <a
              href="/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden tablet:block relative group transition-all duration-200 hover:text-blue-600"
            >
              <span>사례·정보</span>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></div>
            </a>
            <a
              href="https://creditncity-c.com/blog/introducing-creditncity-law-office"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden tablet:flex relative group transition-all duration-200 hover:text-blue-600"
              id="counseling_click"
            >
              <div>상담 안내</div>
              <IcArrowDiagonal />
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></div>
            </a>
          </nav>
        </div>

        {/* Desktop/Tablet Right Side */}
        <div>
          {displayUserType === "anonymous" ? (
            <Link to="/signin">
              <Button className="bg-c-primary text-white font-bold rounded-full hover:bg-[#003399] hover:shadow-lg transition-all duration-200 transform text-xs tablet:text-sm desktop:text-base">
                로그인
              </Button>
            </Link>
          ) : (
            // Same height as the logged-out button (h-9): the switch after hydration must not shift the page
            <div className="hidden tablet:flex items-center gap-2 h-9">
              <Link to="/chatlist" target="_blank">
                <Button className="bg-c-primary text-white font-bold rounded-full hover:bg-[#003399] hover:shadow-lg transition-all duration-200 transform text-xs tablet:text-sm desktop:text-base">
                  내 사건 관리
                </Button>
              </Link>
              <DropdownMenuAvatar userInfo={userInfo} />
            </div>
          )}
        </div>

        {/* Mobile Burger Menu Button */}
        <div className="tablet:hidden flex items-center gap-4">
          {!isMobileMenuOpen && displayUserType !== "anonymous" && (
            <Link to="/chatlist" target="_blank">
              <Button className="bg-c-primary text-white font-bold rounded-full hover:bg-[#003399] hover:shadow-lg transition-all duration-200 transform text-xs tablet:text-sm desktop:text-base">
                내 사건 관리
              </Button>
            </Link>
          )}
          <button
            className="p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-[18px] w-[18px]" />
            ) : (
              <Menu className="h-[24px] w-[24px]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={closeMobileMenu}
        />
      )}

      {/* Hamburger Sheet */}
      <div
        className={`md:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <img
              src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/logos/header-logo.webp"
              alt="채권도시 법률사무소"
              style={{ height: "1.5rem" }}
            />
            <button
              onClick={closeMobileMenu}
              className="p-2"
              aria-label="Close mobile menu"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 flex flex-col justify-between p-4">
            {/* Navigation Links */}
            <nav className="space-y-4">
              <Link
                to="/company"
                className="py-3 text-lg font-semibold border-b border-gray-100 cursor-pointer hover:text-blue-600 transition-colors block"
                onClick={closeMobileMenu}
              >
                대표 변호사
              </Link>
              <a
                href="/blog"
                className="py-3 text-lg font-semibold border-b border-gray-100 cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-2"
                onClick={closeMobileMenu}
              >
                <span>사례·정보</span>
              </a>
              <a
                href="https://creditncity-c.com/blog/introducing-creditncity-law-office"
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 text-lg font-semibold border-b border-gray-100 cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-2"
                onClick={closeMobileMenu}
              >
                <span>상담 안내</span>
                <IcArrowDiagonal />
              </a>
            </nav>

            {/* Mobile Auth Section */}
            <div className="space-y-3">
              {displayUserType === "anonymous" ? (
                <Link to="/signin" onClick={closeMobileMenu}>
                  <Button className="w-full bg-c-primary text-white font-bold rounded-full hover:bg-[#003399] hover:shadow-lg transition-all duration-200 transform">
                    로그인
                  </Button>
                </Link>
              ) : (
                <div className="space-y-3">
                  <Button
                    className="w-full bg-c-primary text-white font-bold rounded-full hover:bg-[#003399] hover:shadow-lg transition-all duration-200 transform"
                    onClick={onLogout}
                  >
                    로그아웃
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
